import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Box, IconButton, Stack, Typography } from "@mui/material";

import { useUserActions } from "store";
import { INotification, INotificationsGroup } from "store/types";
import { useClickHandler } from "hooks/notifications-hooks/use-click-handler";
import { useFormatDateHook } from "hooks";
import { NotificationIcon } from "../Notification-icon/NotificationIcon";
import { NotificationMenu } from "../Notification-menu/NotificationMenu";

interface Props {
  notificationsGroup: INotificationsGroup;
  closeList: () => void;
  elementIndex: number;
}

export const ListGroup: React.FC<Props> = ({ notificationsGroup, closeList, elementIndex }) => {
  const formatDate = useFormatDateHook();
  const { status } = notificationsGroup;
  const { updateNotificationGroupAction, deleteNotificationGroupAction, updateNotificationsAction } = useUserActions();

  const contentRef = useRef<HTMLDListElement>(null);
  const [contentLength, setContentLength] = useState<string>("0px");
  const [isContentOpen, setIsContentOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isContentOpen && contentRef.current) {
      setContentLength(contentRef?.current?.scrollHeight + 100 + "px");
    } else {
      setContentLength("0px");
    }
  }, [isContentOpen]);

  const idsInRow = useCallback((notifications: INotification[]): string => {
    return notifications
      .reduce((acc: number[], notification: INotification) => {
        return [...acc, notification.id];
      }, [])
      .join();
  }, []);

  const markAsRead = useCallback(() => {
    updateNotificationGroupAction({
      ids: idsInRow(notificationsGroup.notifications),
      status: "read",
      elementIndex
    });
  }, [updateNotificationGroupAction]);

  const markAsUnread = useCallback(() => {
    updateNotificationGroupAction({
      ids: idsInRow(notificationsGroup.notifications),
      status: "unread",
      elementIndex
    });
  }, [updateNotificationGroupAction]);

  const removeNotification = useCallback(() => {
    deleteNotificationGroupAction({
      ids: idsInRow(notificationsGroup.notifications),
      elementIndex
    });
  }, [deleteNotificationGroupAction]);

  const markAsReadOne = useCallback(
    (id: number) => {
      updateNotificationsAction(id, "read");
    },
    [updateNotificationsAction]
  );

  const notificationClickHandler = useClickHandler();

  const isAllNotificationsRead = useMemo(() => {
    return !!notificationsGroup.notifications.find((notification: INotification) => !notification.read_at);
  }, [notificationsGroup]);

  return (
    <Stack>
      <Stack
        direction="row"
        sx={{
          p: "12px 20px 10px 20px",
          bgcolor: "rgba(38, 166, 174, 0.04)",
          borderBottom: "1px solid #C7C7C7",
          gap: "12px"
        }}
      >
        <NotificationIcon type={notificationsGroup.action} />
        <Stack
          sx={{ justifyContent: "space-between", flexGrow: 1 }}
          onClick={() => {
            if (status === "unread") {
              markAsRead();
            }
            if (notificationsGroup.notifications[0]) {
              notificationClickHandler(notificationsGroup.notifications[0]);
              closeList();
            }
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#597380",
              overflow: "hidden"
            }}
            dangerouslySetInnerHTML={{ __html: `<p>${notificationsGroup.text}</p>` }}
          />
          <Typography sx={{ fontSize: "12px", color: "#8a9ca5" }}>
            {formatDate({ date: notificationsGroup?.notifications?.[0]?.created_at, format: "DD MMM, YYYY" })}
          </Typography>
        </Stack>
        <Stack sx={{ alignItems: "center", gap: "10px" }}>
          <NotificationMenu
            remove={removeNotification}
            {...(isAllNotificationsRead ? { markAsRead } : { markAsUnread })}
          />
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#F6FBFC",
              border: "1px solid #597380",
              borderRadius: "8px",
              height: 16,
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "16px",
              color: "#597380",
              p: "0 6px"
            }}
          >
            {notificationsGroup.notifications.length}
          </Stack>
          <IconButton
            sx={{ ...(isContentOpen && { transform: "rotate(180deg)" }), transition: "all 0.3s linear" }}
            onClick={() => setIsContentOpen(!isContentOpen)}
            size="sm"
          >
            <ExpandLessIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Box
        sx={{
          overflow: "hidden",
          transition: "all 0.3s linear"
        }}
        style={{ maxHeight: contentLength }}
        ref={contentRef}
      >
        {notificationsGroup.notifications.map((notification: INotification) => (
          <Stack
            onClick={() => {
              if (!notification.read_at) {
                markAsReadOne(notification.id);
              }
              notificationClickHandler(notification);
              closeList();
            }}
            sx={{
              justifyContent: "space-between",
              minHeight: 76,
              backgroundColor: notification.read_at ? "transparent" : "rgba(38, 166, 174, 0.04)",
              cursor: "pointer",
              borderBottom: "1px solid #C7C7C7",
              p: "16px 48px 12px 56px"
            }}
            key={notification.id}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "#597380",
                overflow: "hidden"
              }}
              dangerouslySetInnerHTML={{ __html: `<p>${notification.text}</p>` }}
            />
            <Typography sx={{ fontSize: "12px", color: "#8a9ca5" }}>
              {formatDate({ date: notification.created_at, format: "HH:mm, MMM DD, YYYY" })}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Stack>
  );
};
